"use client";

import React, { useCallback, useMemo } from "react";

// ============================================================
// MAIN FOOTER INDEX - Export all footer components and utilities
// ============================================================
// This file serves as the single entry point for all footer-related
// imports and provides the FooterController for dynamic loading.
//
// USAGE:
//   import { FooterController, FOOTER_VARIANTS } from ".../footer";
//   <FooterController variant="corporate" content={...} />
// ============================================================

// Types
export type { FooterVariant, FooterContent, SocialLink, NavLink, LinkGroup } from "./types";
export type { FooterRegistryEntry, FooterLoadingState } from "./types";

// Design tokens
export * from "./tokens";

// Context
export { FooterProvider, useFooterContext } from "./FooterContext";

// Controller and Registry - import for internal use and re-export
import {
  FooterController,
  FOOTER_REGISTRY,
  FOOTER_VARIANTS,
} from "./FooterController";

export { FooterController, FOOTER_REGISTRY, FOOTER_VARIANTS };

// Base components
export {
  SocialLinks,
  NavLinks,
  LinkGroupComponent,
  BrandArea,
  Newsletter,
  Divider,
  Copyright,
  getSocialIcon,
} from "./BaseFooter";

// Default content generator
export { getDefaultFooterContent } from "./types";

// Variants - Individual exports for direct imports
export { CorporateFooter } from "./variants/CorporateFooter";
export { MinimalFooter } from "./variants/MinimalFooter";
export { BigBrandFooter } from "./variants/BigBrandFooter";
export { ThreeColumnFooter } from "./variants/ThreeColumnFooter";
export { ModernFooter } from "./variants/ModernFooter";
export { UniversityFooter } from "../../university";
export { EducaeetFooter } from "../../educaeet";

// ============================================================
// CONVENIENCE: Default export for quick usage
// ============================================================

interface QuickFooterProps {
  variant?: "corporate" | "minimal" | "big-brand" | "three-column" | "modern" | "university" | "educaeet";
  content?: Record<string, any>;
  isEditing?: boolean;
  className?: string;
}

// Simple wrapper for quick usage without full configuration
export function QuickFooter({
  variant = "corporate",
  content,
  isEditing = false,
  className = "",
}: QuickFooterProps) {
  const FooterComponent = useMemo(
    () => {
      const components: Record<string, React.ComponentType<any>> = {
        corporate: require("./variants/CorporateFooter").default,
        minimal: require("./variants/MinimalFooter").default,
        "big-brand": require("./variants/BigBrandFooter").default,
        "three-column": require("./variants/ThreeColumnFooter").default,
        modern: require("./variants/ModernFooter").default,
        university: require("../../university/universityfooter/UniversityFooter").default,
        educaeet: require("../../educaeet/educaeetfooter/EducaeetFooter").EducaeetFooter,
      };
      return components[variant];
    },
    [variant]
  );

  return (
    <div className={className}>
      <FooterComponent content={content} isEditing={isEditing} />
    </div>
  );
}

// ============================================================
// HOOK: useFooterVariant
// ============================================================

/**
 * Hook to get footer variant configuration
 * @param variant - The footer variant identifier
 * @returns Variant info including label, description, and preview
 */
export function useFooterVariant(variant: string) {
  return useMemo(() => {
    return FOOTER_VARIANTS.find((v) => v.id === variant) || FOOTER_VARIANTS[0];
  }, [variant]);
}

// ============================================================
// FOOTER PREVIEW COMPONENT (for template selection)
// ============================================================

interface FooterPreviewProps {
  variant: string;
  size?: "small" | "medium" | "large";
}

export const FooterPreview: React.FC<FooterPreviewProps> = ({
  variant,
  size = "medium",
}) => {
  const variantInfo = useFooterVariant(variant);

  const sizeClasses = {
    small: "w-32 h-16 text-xs",
    medium: "w-48 h-24 text-sm",
    large: "w-64 h-32 text-base",
  };

  return (
    <div
      className={`
        ${sizeClasses[size]}
        bg-gradient-to-br from-slate-800 to-slate-900
        rounded-lg border border-slate-700
        flex flex-col items-center justify-center
        text-white
      `}
      title={variantInfo.description}
    >
      <span className="text-2xl mb-1">{variantInfo.preview}</span>
      <span className="font-medium">{variantInfo.label}</span>
    </div>
  );
};

// ============================================================
// FOOTER SWITCHER COMPONENT (for template selection UI)
// ============================================================

interface FooterSwitcherProps {
  selectedVariant: string;
  onSelect: (variant: string) => void;
  className?: string;
}

export const FooterSwitcher: React.FC<FooterSwitcherProps> = ({
  selectedVariant,
  onSelect,
  className = "",
}) => {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 ${className}`}>
      {FOOTER_VARIANTS.map((variant) => (
        <button
          key={variant.id}
          onClick={() => onSelect(variant.id)}
          className={`
            p-4 rounded-xl border-2 text-left transition-all
            ${selectedVariant === variant.id
              ? "border-blue-500 bg-blue-500/10"
              : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
            }
          `}
          aria-pressed={selectedVariant === variant.id}
        >
          <div className="text-3xl mb-2">{variant.preview}</div>
          <div className="font-semibold text-white mb-1">{variant.label}</div>
          <div className="text-xs text-slate-400">{variant.description}</div>
        </button>
      ))}
    </div>
  );
};

// Default export is the FooterController
export default FooterController;
